import {
  BallAction,
  FrameStencil,
  MarkerBall,
  MarkerPlayer,
  MarkerType,
  PlayerAction,
  PossessionAction,
  StencilType,
} from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { findClosestStencil } from "../find-closest";
import {
  getBallActions,
  getHandoffActions,
  isBallAction,
  isBallOrPossessionAction,
  isPlayerAction,
  isPlayerOrPossessionAction,
  isPossessionAction,
} from "../frame-stencil-filters";
import { getDurationFromAction } from "../get-duration-from-action";

export interface CheckPlayerPossessionActionStartPointProps<T extends PlayerAction | PossessionAction> {
  stencils: FrameStencil[];
  stencilsToCheck: (PlayerAction | BallAction | PossessionAction | MarkerPlayer | MarkerBall)[];
  action: Omit<T, "startTime" | "endTime"> & Partial<Pick<T, "startTime" | "endTime">>;
}

export const checkPlayerPossessionActionStartPoint = <T extends PlayerAction | PossessionAction>({
  stencils,
  stencilsToCheck,
  action,
}: CheckPlayerPossessionActionStartPointProps<T>): { action: T; stencils: FrameStencil[] } => {
  const newActionDuration = getDurationFromAction(action);

  // find closest stencil to new startPoint
  const closestStencilToStartPoint = findClosestStencil({
    target: { id: action.id, point: action.pos },
    stencils: stencilsToCheck,
    stencilsPointAccessor: (s) => (s.stencilType === StencilType.MARKER ? s.pos : s.endPos),
  });

  const newActionStartTime: number = ((): number => {
    if (!closestStencilToStartPoint) return action?.startTime || 0;

    switch (closestStencilToStartPoint.stencilType) {
      case StencilType.MARKER: {
        const passToPlayer = [
          ...getBallActions(stencils),
          ...getHandoffActions(stencils).filter((h) => h.nextAction !== action.id),
        ].find((b) => b.nextPlayer === closestStencilToStartPoint.id);
        return Math.max(passToPlayer?.endTime || 0, action?.startTime || 0);
      }

      case StencilType.ACTION:
        return Math.max(closestStencilToStartPoint.endTime, action?.startTime || 0);
    }
  })();

  const newActionEndTime = newActionStartTime + newActionDuration;

  const actionWithTimes = { ...action, startTime: newActionStartTime, endTime: newActionEndTime } as T;

  // splice updated action into stencil on startPoint
  const stencilsWithUpdatedPrevStencil: FrameStencil[] = ((): FrameStencil[] => {
    if (!closestStencilToStartPoint) return stencils;

    switch (closestStencilToStartPoint.stencilType) {
      case StencilType.MARKER: {
        switch (closestStencilToStartPoint.markerType) {
          case MarkerType.PLAYER: {
            const stencilsWithMovements = sliceItemUpdate({
              arr: stencils,
              item: { ...closestStencilToStartPoint, movements: [{ id: actionWithTimes.id }] },
            });

            if (isPossessionAction(actionWithTimes)) {
              const passesToPlayer = getBallActions(stencilsWithMovements).find(
                (b) => b.nextPlayer === closestStencilToStartPoint.id
              );
              return passesToPlayer
                ? sliceItemUpdate({
                    arr: stencilsWithMovements,
                    item: { ...passesToPlayer, nextBallAction: actionWithTimes.id },
                  })
                : stencilsWithMovements;
            } else return stencilsWithMovements;
          }

          default:
            return stencils;
        }
      }

      case StencilType.ACTION: {
        if (isPlayerAction(closestStencilToStartPoint)) {
          return sliceItemUpdate({
            arr: stencils,
            item: { ...closestStencilToStartPoint, nextPlayerAction: actionWithTimes.id },
          });
        } else if (isBallAction(closestStencilToStartPoint)) {
          return sliceItemUpdate({
            arr: stencils,
            item: { ...closestStencilToStartPoint, nextBallAction: actionWithTimes.id },
          });
        } else if (isPossessionAction(closestStencilToStartPoint)) {
          return sliceItemUpdate({
            arr: stencils,
            item: {
              ...closestStencilToStartPoint,
              ...(isPlayerOrPossessionAction(actionWithTimes) && { nextPlayerAction: actionWithTimes.id }),
              ...(isBallOrPossessionAction(actionWithTimes) && { nextBallAction: action.id }),
            },
          });
        } else return stencils;
      }
    }
  })();

  return { action: actionWithTimes, stencils: stencilsWithUpdatedPrevStencil };
};
